import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash, PencilSimple, ArrowLeft, Lightning } from "@phosphor-icons/react";

const TRIGGERS = [["on_create", "When a ticket is created"], ["on_status_change", "When status changes"], ["on_attribute_match", "On create or status change (match attributes)"], ["on_overdue", "When a ticket becomes overdue (past due date)"]];
const OPS = [["eq", "equals"], ["ne", "does not equal"], ["contains", "contains"], ["is_empty", "is empty"], ["not_empty", "is not empty"]];
const ACTIONS = [["assign", "Assign to"], ["set_priority", "Set priority"], ["escalate_priority", "Escalate priority (bump one level)"], ["set_status", "Set status"], ["add_tag", "Add tag"], ["notify", "Notify"]];
const CATS = [["any", "Any category"], ["op", "O&P"], ["cip", "CIP"], ["security", "Security"]];
const PRIORITIES = ["low", "normal", "high"];

const blank = () => ({ name: "", enabled: true, trigger: "on_attribute_match", category: "any", conditions: [], actions: [] });

// Admin IF/THEN workflow-automation rule builder for ClaroFlow.
export default function FlowRulesDialog({ open, onOpenChange, users = [], customFields = [], statuses = [] }) {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);   // null = list view, object = editor
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("rules");         // rules | activity
  const [activity, setActivity] = useState([]);

  const fieldOptions = [
    ["category", "Category"], ["status", "Status"], ["priority", "Priority"],
    ["assignee", "Assignee"], ["tag", "Tag"],
    ...customFields.map((f) => [`custom.${f.key}`, `Custom: ${f.label}`]),
  ];

  const load = () => api.get("/flow/rules").then((r) => setRules(r.data.rules || [])).catch(() => {});
  const loadActivity = () => api.get("/flow/rules/activity?limit=100").then((r) => setActivity(r.data.activity || [])).catch(() => {});
  useEffect(() => { if (open) { load(); setEditing(null); setTab("rules"); } }, [open]);
  useEffect(() => { if (open && tab === "activity") loadActivity(); }, [open, tab]);

  const toggleEnabled = async (rule) => {
    try { await api.put(`/flow/rules/${rule.id}`, { ...rule, enabled: !rule.enabled }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeRule = async (rid) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/flow/rules/${rid}`); load(); toast.success("Rule deleted");
  };
  const save = async () => {
    if (!editing.name.trim()) return toast.error("Rule name required");
    setBusy(true);
    try {
      if (editing.id) await api.put(`/flow/rules/${editing.id}`, editing);
      else await api.post("/flow/rules", editing);
      toast.success("Rule saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setC = (i, patch) => setEditing((r) => ({ ...r, conditions: r.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const setA = (i, patch) => setEditing((r) => ({ ...r, actions: r.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));

  const actionValueInput = (a, i) => {
    if (a.type === "assign") return (
      <Select value={a.value || ""} onValueChange={(v) => setA(i, { value: v })}>
        <SelectTrigger className="rounded-none"><SelectValue placeholder="user" /></SelectTrigger>
        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
      </Select>);
    if (a.type === "set_priority") return (
      <Select value={a.value || ""} onValueChange={(v) => setA(i, { value: v })}>
        <SelectTrigger className="rounded-none"><SelectValue placeholder="priority" /></SelectTrigger>
        <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
      </Select>);
    if (a.type === "set_status") return (
      <Select value={a.value || ""} onValueChange={(v) => setA(i, { value: v })}>
        <SelectTrigger className="rounded-none"><SelectValue placeholder="status" /></SelectTrigger>
        <SelectContent>{statuses.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>);
    if (a.type === "escalate_priority") return <span className="text-xs text-muted-foreground self-center">low → normal → high</span>;
    return <Input value={a.value || ""} onChange={(e) => setA(i, { value: e.target.value })} className="rounded-none" placeholder={a.type === "notify" ? "email or 'assignee'" : "tag"} />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="flow-rules-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lightning size={18} />Workflow Automation</DialogTitle></DialogHeader>

        {!editing ? (
          <div className="space-y-3">
            <div className="flex gap-2" data-testid="flow-rules-tabs">
              <button data-testid="flow-tab-rules" onClick={() => setTab("rules")} className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${tab === "rules" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>Rules</button>
              <button data-testid="flow-tab-activity" onClick={() => setTab("activity")} className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${tab === "activity" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>Activity log</button>
            </div>
            {tab === "rules" ? (<>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">IF/THEN rules that auto-route, re-prioritise, re-status, tag, or notify when tickets are created or change.</p>
              <Button data-testid="flow-rule-new" size="sm" onClick={() => setEditing(blank())} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New rule</Button>
            </div>
            <div className="space-y-2" data-testid="flow-rules-list">
              {rules.length === 0 && <p className="text-xs text-muted-foreground italic">No rules yet.</p>}
              {rules.map((r) => (
                <div key={r.id} data-testid={`flow-rule-${r.id}`} className="border border-border p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{(TRIGGERS.find((t) => t[0] === r.trigger) || [])[1]} · {r.conditions.length} condition(s) · {r.actions.length} action(s)</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Switch data-testid={`flow-rule-toggle-${r.id}`} checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} />
                    <button data-testid={`flow-rule-edit-${r.id}`} onClick={() => setEditing({ ...r })} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                    <button data-testid={`flow-rule-delete-${r.id}`} onClick={() => removeRule(r.id)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
            </>) : (
            <div data-testid="flow-activity-log">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-muted-foreground">Most recent rule firings — which rule ran, on which ticket, and when.</p>
                <button data-testid="flow-activity-refresh" onClick={loadActivity} className="text-xs text-muted-foreground hover:text-foreground">Refresh</button>
              </div>
              {activity.length === 0 ? <p className="text-xs text-muted-foreground italic">No automation activity yet.</p> : (
                <div className="border border-border divide-y divide-border max-h-96 overflow-y-auto">
                  {activity.map((a) => (
                    <div key={a.id} data-testid={`flow-activity-${a.id}`} className="px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold truncate">{a.rule_name}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">{(a.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <div className="text-muted-foreground mt-0.5">
                        {a.source === "sla" ? "SLA (overdue)" : (TRIGGERS.find((t) => t[0] === a.trigger) || [])[1] || a.trigger}
                        {" → "}<span className="truncate">{a.ticket_title || a.ticket_id}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(a.actions || []).map((ac, i) => (
                          <span key={i} className="border border-border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium">
                            {((ACTIONS.find((x) => x[0] === ac.type) || [])[1] || ac.type)}{ac.value ? `: ${ac.value}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <button onClick={() => setEditing(null)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft size={13} />Back to rules</button>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Rule name</Label><Input data-testid="flow-rule-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="text-xs">Applies to</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Trigger</Label>
              <Select value={editing.trigger} onValueChange={(v) => setEditing({ ...editing, trigger: v })}>
                <SelectTrigger data-testid="flow-rule-trigger" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs">IF (all conditions match)</Label>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="flow-cond-add" onClick={() => setEditing({ ...editing, conditions: [...editing.conditions, { field: "priority", op: "eq", value: "" }] })}><Plus size={13} /></Button></div>
              <div className="space-y-2">
                {editing.conditions.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`flow-cond-${i}`}>
                    <Select value={c.field} onValueChange={(v) => setC(i, { field: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{fieldOptions.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={c.op} onValueChange={(v) => setC(i, { op: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{OPS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    {["is_empty", "not_empty"].includes(c.op)
                      ? <span className="text-xs text-muted-foreground">—</span>
                      : <Input value={c.value || ""} onChange={(e) => setC(i, { value: e.target.value })} className="rounded-none" placeholder="value" />}
                    <button onClick={() => setEditing({ ...editing, conditions: editing.conditions.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>
                  </div>
                ))}
                {editing.conditions.length === 0 && <p className="text-[11px] text-muted-foreground italic">No conditions — the rule matches every ticket for this trigger.</p>}
              </div>
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs">THEN (do)</Label>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="flow-action-add" onClick={() => setEditing({ ...editing, actions: [...editing.actions, { type: "set_priority", value: "" }] })}><Plus size={13} /></Button></div>
              <div className="space-y-2">
                {editing.actions.map((a, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`flow-action-${i}`}>
                    <Select value={a.type} onValueChange={(v) => setA(i, { type: v, value: "" })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{ACTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    {actionValueInput(a, i)}
                    <button onClick={() => setEditing({ ...editing, actions: editing.actions.filter((_, idx) => idx !== i) })} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>
                  </div>
                ))}
                {editing.actions.length === 0 && <p className="text-[11px] text-muted-foreground italic">Add at least one action.</p>}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm"><Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />Enabled</label>
              <Button data-testid="flow-rule-save" disabled={busy} onClick={save} className="rounded-none bg-primary hover:bg-primary/90 ml-auto">Save rule</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
